// src/components/group/MembersSection.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function MembersSection({ 
  group, 
  groupId, 
  token, 
  apiUrl, 
  userPlan, 
  onMemberClick, 
  onUpdated,
  isOpen,
  onToggle
}) {
  const { t } = useTranslation();
  const [newMember, setNewMember] = useState({ name: '', email: '' });
  
  // Modal states
  const [showSelfRegModal, setShowSelfRegModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  
  const [allGroups, setAllGroups] = useState([]);
  const [selectedSourceGroup, setSelectedSourceGroup] = useState('');
  const [copying, setCopying] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  const maxParticipants = userPlan === 'free' ? 15 : 50;
  const canAddMore = group.participants.length < maxParticipants;
  const isPremium = userPlan === 'premium' || userPlan === 'pro' || userPlan === 'enterprise';

  // Self-registration link
  const selfRegisterUrl = `${window.location.origin}/register/${groupId}`;

  const handleAddMember = async (e) => {
    e.preventDefault();
    
    if (!canAddMore) {
      alert(t('members.maxReached', { 
        max: maxParticipants, 
        plan: userPlan === 'free' ? t('members.freePlan') : t('members.premiumPlan')
      }));
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/groups/${groupId}/participants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newMember)
      });

      if (response.ok) {
        setNewMember({ name: '', email: '' });
        onUpdated();
        alert(t('members.memberAdded'));
      } else {
        const data = await response.json();
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error adding member:', error);
      alert(t('members.couldNotAddMember'));
    }
  };

  // ⭐ NY FUNKTION: Toggle nyckelperson (MED DEBUGGING)
  const handleToggleKeyPerson = async (participantId, currentStatus, e) => {
    console.log('🔍 handleToggleKeyPerson called!');
    console.log('  participantId:', participantId);
    console.log('  currentStatus:', currentStatus);
    console.log('  isPremium:', isPremium);
    console.log('  apiUrl:', apiUrl);
    console.log('  groupId:', groupId);
    console.log('  token:', token ? 'exists (length: ' + token.length + ')' : 'MISSING!');
    
    if (!isPremium) {
      console.log('❌ Not premium!');
      alert(t('members.keyPersonPremiumOnly'));
      return;
    }

    const url = `${apiUrl}/api/groups/${groupId}/participants/${participantId}`;
    console.log('🚀 Sending PUT request to:', url);
    console.log('  Body:', { isKeyPerson: !currentStatus });

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          isKeyPerson: !currentStatus
        })
      });

      console.log('📥 Response received!');
      console.log('  Status:', response.status);
      console.log('  Status Text:', response.statusText);
      console.log('  OK:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ SUCCESS! Response data:', data);
        console.log('🔄 Calling onUpdated() to refresh...');
        onUpdated();
      } else {
        const errorData = await response.json();
        console.error('❌ Error response:', errorData);
        alert(t('members.couldNotUpdateKeyPerson') + '\n\nStatus: ' + response.status + '\nError: ' + JSON.stringify(errorData));
      }
    } catch (error) {
      console.error('❌ Catch error:', error);
      console.error('  Error name:', error.name);
      console.error('  Error message:', error.message);
      alert(t('members.couldNotUpdateKeyPerson') + '\n\nError: ' + error.message);
    }
  };

  const handleCopySelfRegLink = () => {
    navigator.clipboard.writeText(selfRegisterUrl);
    alert(t('members.linkCopied'));
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadStatus(t('members.readingFile'));

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setUploadStatus(t('members.fileEmpty'));
        return;
      }

      const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
      const nameIndex = headers.findIndex(h => h === 'namn' || h === 'name');
      const emailIndex = headers.findIndex(h => h === 'email' || h === 'e-post');

      if (nameIndex === -1 || emailIndex === -1) {
        setUploadStatus(t('members.csvMustContain'));
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const name = values[nameIndex];
        const email = values[emailIndex];

        if (!name || !email || !email.includes('@')) {
          errorCount++;
          continue;
        }

        try {
          await fetch(`${apiUrl}/api/groups/${groupId}/participants`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ name, email })
          });
          successCount++;
        } catch {
          errorCount++;
        }
      }

      setUploadStatus(t('members.uploadResult', { 
        success: successCount,
        error: errorCount 
      }));
      onUpdated();
      
      setTimeout(() => setUploadStatus(''), 5000);
      e.target.value = '';
    } catch (error) {
      setUploadStatus(t('members.couldNotReadFile'));
      console.error('CSV upload error:', error);
    }
  };

  const downloadCSVTemplate = () => {
    const template = `Namn,Email
Anna Andersson,anna@example.com
Björn Bengtsson,bjorn@example.com
Cecilia Carlsson,cecilia@example.com`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'medlemslista_mall.csv';
    link.click();
  };

  const handleOpenCopyModal = async () => {
    setSelectedSourceGroup('');
    setCopying(false);
    
    try {
      const response = await fetch(`${apiUrl}/api/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const groups = await response.json();
        const otherGroups = groups.filter(g => g._id !== groupId);
        setAllGroups(otherGroups);
        setShowCopyModal(true);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      alert(t('members.couldNotFetchGroups'));
    }
  };

  const handleCopyMembers = async () => {
    if (!selectedSourceGroup) {
      alert(t('members.selectGroupToCopy'));
      return;
    }

    setCopying(true);

    try {
      const sourceGroup = allGroups.find(g => g._id === selectedSourceGroup);
      
      if (!sourceGroup || !sourceGroup.participants || sourceGroup.participants.length === 0) {
        alert(t('members.noMembersInGroup'));
        setCopying(false);
        return;
      }

      let successCount = 0;
      for (const member of sourceGroup.participants) {
        try {
          const response = await fetch(`${apiUrl}/api/groups/${groupId}/participants`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              name: member.name,
              email: member.email
            })
          });

          if (response.ok) {
            successCount++;
          }
        } catch (error) {
          console.error(`Kunde inte kopiera ${member.name}:`, error);
        }
      }

      setShowCopyModal(false);
      onUpdated();
      
      alert(t('members.membersCopied', { 
        success: successCount, 
        total: sourceGroup.participants.length 
      }));
    } catch (error) {
      console.error('Error copying members:', error);
      alert(t('members.couldNotCopyMembers'));
    } finally {
      setCopying(false);
    }
  };

  return (
    <section className="settings-section">
      <div className="section-header" onClick={onToggle}>
        <h2>
          {t('members.title', { 
            current: group.participants?.length || 0, 
            max: maxParticipants 
          })}
        </h2>
        <span className="toggle-icon">{isOpen ? '▼' : '▶'}</span>
      </div>
      {isOpen && (
        <div>
          {!canAddMore && (
            <div className="warning-box" style={{ marginBottom: '20px' }}>
              {t('members.limitWarning', { max: maxParticipants })}
              {userPlan === 'free' && ` ${t('members.upgradeForMore')}`}
            </div>
          )}

          {/* ⭐ Premium feature info */}
          {isPremium && (
            <div style={{
              background: '#fff9e6',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                {t('members.keyPersonFeature')}
              </div>
              <div style={{ color: '#666' }}>
                {t('members.keyPersonDesc')}
              </div>
            </div>
          )}

          {/* ⭐ UPPDATERAD Member list */}
          <div className="members-list">
            {group.participants && group.participants.map((member) => (
              <div 
                key={member._id} 
                className={`member-row ${member.isKeyPerson ? 'key-person-row' : ''}`}
                onClick={() => onMemberClick(member)}
              >
                <span className="member-status">✓</span>
                <div className="member-info-wrapper">
                  <span className="member-name">
                    {member.isKeyPerson && <span className="key-person-badge">⭐</span>}
                    {member.name}
                  </span>
                  <span className="member-email">{member.email}</span>
                </div>
                {isPremium && (
                  <button
                    className={`key-person-toggle ${member.isKeyPerson ? 'is-key-person' : ''}`}
                    onClick={(e) => {
  e.stopPropagation();
  e.preventDefault();
  console.log('🎯 Star clicked!', member.name);
  handleToggleKeyPerson(member._id, member.isKeyPerson, e);
}}
                    title={member.isKeyPerson ? t('members.removeKeyPerson') : t('members.makeKeyPerson')}
                  >
                    ⭐
                  </button>
                )}
                <span className="member-menu">⋮</span>
              </div>
            ))}
          </div>

          {/* Add member form */}
          <form onSubmit={handleAddMember} className="add-member-form">
            <input
              type="text"
              placeholder={t('members.namePlaceholder')}
              value={newMember.name}
              onChange={(e) => setNewMember({...newMember, name: e.target.value})}
              required
              disabled={!canAddMore}
              style={{ height: '42px' }}
            />
            <input
              type="email"
              placeholder={t('members.emailPlaceholder')}
              value={newMember.email}
              onChange={(e) => setNewMember({...newMember, email: e.target.value})}
              required
              disabled={!canAddMore}
              style={{ height: '42px' }}
            />
            <button 
              type="submit"
              disabled={!canAddMore}
              className="btn-add"
              style={{ height: '42px' }}
            >
              {t('members.addButton')}
            </button>
          </form>

          {/* Action links */}
          <div style={{ 
            textAlign: 'center',
            marginTop: '15px',
            fontSize: '15px',
            color: '#666',
            padding: '0 25px'
          }}>
            <button 
              onClick={() => setShowSelfRegModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#6c63ff',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '15px',
                padding: '0',
                fontWeight: '500'
              }}
            >
              {t('members.shareSelfRegLink')}
            </button>
            
            {isPremium && (
              <>
                <span style={{ margin: '0 12px', color: '#ccc' }}>|</span>
                <button 
                  onClick={() => setShowCSVModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6c63ff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '15px',
                    padding: '0',
                    fontWeight: '500'
                  }}
                >
                  {t('members.uploadCSV')}
                </button>
              </>
            )}
            
            <span style={{ margin: '0 12px', color: '#ccc' }}>|</span>
            
            <button 
              onClick={handleOpenCopyModal}
              style={{
                background: 'none',
                border: 'none',
                color: '#6c63ff',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '15px',
                padding: '0',
                fontWeight: '500'
              }}
            >
              {t('members.copyMembers')}
            </button>
          </div>

          {/* Self-registration modal */}
          {showSelfRegModal && (
            <div 
              onClick={() => setShowSelfRegModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
              }}
            >
              <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '500px',
                  width: '90%',
                  padding: '30px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  position: 'relative'
                }}
              >
                <button 
                  onClick={() => setShowSelfRegModal(false)}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    width: '32px',
                    height: '32px',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    color: '#999',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
                
                <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#333' }}>
                  {t('members.selfRegModalTitle')}
                </h2>
                <p style={{ color: '#666', margin: '0 0 20px 0', fontSize: '14px' }}>
                  {t('members.selfRegModalDesc')}
                </p>
                
                <div style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <input 
                    type="text" 
                    value={selfRegisterUrl} 
                    readOnly 
                    style={{ 
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#f9f9f9',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <button 
                    onClick={handleCopySelfRegLink}
                    style={{
                      padding: '10px 20px',
                      background: '#6c63ff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {t('members.copyButton')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* CSV upload modal */}
          {showCSVModal && (
            <div 
              onClick={() => setShowCSVModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
              }}
            >
              <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '500px',
                  width: '90%',
                  padding: '30px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  position: 'relative'
                }}
              >
                <button 
                  onClick={() => setShowCSVModal(false)}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    width: '32px',
                    height: '32px',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    color: '#999',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
                
                <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#333' }}>
                  {t('members.csvModalTitle')}
                </h2>
                <p style={{ color: '#666', margin: '0 0 20px 0', fontSize: '14px' }}>
                  {t('members.csvModalDesc')}
                </p>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <label style={{
                    flex: 1,
                    padding: '12px 20px',
                    backgroundColor: '#6c63ff',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    {t('members.selectCSVFile')}
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                  
                  <button
                    type="button"
                    onClick={downloadCSVTemplate}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {t('members.downloadTemplate')}
                  </button>
                </div>

                {uploadStatus && (
                  <div style={{
                    padding: '10px 15px',
                    borderRadius: '5px',
                    backgroundColor: uploadStatus.includes('✅') ? '#d4edda' : 
                                   uploadStatus.includes('❌') ? '#f8d7da' : '#fff3cd',
                    color: uploadStatus.includes('✅') ? '#155724' : 
                           uploadStatus.includes('❌') ? '#721c24' : '#856404',
                    border: `1px solid ${uploadStatus.includes('✅') ? '#c3e6cb' : 
                                       uploadStatus.includes('❌') ? '#f5c6cb' : '#ffeaa7'}`,
                    fontSize: '14px'
                  }}>
                    {uploadStatus}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Copy members modal */}
          {showCopyModal && (
            <div 
              onClick={() => setShowCopyModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
              }}
            >
              <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '500px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflow: 'auto',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  position: 'relative'
                }}
              >
                <button 
                  onClick={() => setShowCopyModal(false)}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    width: '32px',
                    height: '32px',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '24px',
                    color: '#999',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
                
                <div style={{ padding: '30px 30px 20px 30px' }}>
                  <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>
                    {t('members.copyModalTitle')}
                  </h2>
                  <p style={{ color: '#666', margin: '10px 0 0 0', fontSize: '14px' }}>
                    {t('members.copyModalDesc')}
                  </p>
                </div>

                <div style={{ padding: '0 30px 30px 30px' }}>
                  {allGroups.length === 0 ? (
                    <div style={{
                      padding: '40px 20px',
                      textAlign: 'center',
                      color: '#999',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px'
                    }}>
                      {t('members.noOtherGroups')}
                    </div>
                  ) : (
                    <div style={{ marginBottom: '20px' }}>
                      {allGroups.map((g) => (
                        <label 
                          key={g._id} 
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '15px',
                            marginBottom: '10px',
                            border: selectedSourceGroup === g._id ? '2px solid #6c63ff' : '2px solid #e0e0e0',
                            borderRadius: '8px',
                            backgroundColor: selectedSourceGroup === g._id ? '#f5f3ff' : 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="radio"
                            name="sourceGroup"
                            value={g._id}
                            checked={selectedSourceGroup === g._id}
                            onChange={(e) => setSelectedSourceGroup(e.target.value)}
                            style={{ marginRight: '15px', cursor: 'pointer' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '3px' }}>
                              {g.groupName}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>
                              {t('members.membersCount', { count: g.participants?.length || 0 })}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  <div style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    justifyContent: 'flex-end',
                    marginTop: '20px',
                    paddingTop: '20px',
                    borderTop: '1px solid #e0e0e0'
                  }}>
                    <button 
                      onClick={() => setShowCopyModal(false)}
                      style={{
                        padding: '10px 20px',
                        background: 'white',
                        color: '#666',
                        border: '2px solid #e0e0e0',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {t('members.cancel')}
                    </button>
                    {allGroups.length > 0 && (
                      <button 
                        onClick={handleCopyMembers}
                        disabled={!selectedSourceGroup || copying}
                        style={{ 
                          padding: '10px 20px',
                          background: (!selectedSourceGroup || copying) ? '#ccc' : '#6c63ff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: (!selectedSourceGroup || copying) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {copying ? t('members.copying') : t('members.copyMembersButton')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default MembersSection;
